/**
 * OpenAI Ads Adapter
 *
 * Adapts event data into the OpenAI measurement format.
 * Returns a unified data structure used for both the browser pixel (oaiq)
 * and the Conversions API (server-side).
 *
 * Documentation:
 * - https://developers.openai.com/ads/measurement-pixel
 * - https://developers.openai.com/ads/conversions-api
 * - https://developers.openai.com/ads/supported-events
 *
 * @since 1.60.0
 */

(function (pmw, undefined) {

	class OpenAIAdapter {

		/**
		 * Adapt event data to the OpenAI format
		 *
		 * @param {object} eventData - Event-specific data (product, items, transaction)
		 * @param {string} eventName - Standardized event name
		 * @param {object} context - Event context (user, page, etc.)
		 * @returns {object|null} Adapted data for OpenAI or null if not configured/supported
		 */
		adapt(eventData, eventName, context) {

			// Check if the OpenAI pixel is configured (has a pixel ID).
			// Don't check 'loaded' status - the pixel might not be loaded yet when the
			// event fires. The event listener checks canNotFireOpenAIEvent() before firing.
			if (!pmwDataLayer?.pixels?.openai?.pixel_id) {
				return null;
			}

			const eventMap = {
				"page_view"     : () => this.adaptPageView(eventData),
				"view_item"     : () => this.adaptViewContent(eventData),
				"add_to_cart"   : () => this.adaptAddToCart(eventData),
				"begin_checkout" : () => this.adaptStartCheckout(eventData),
				"purchase"       : () => this.adaptPurchase(eventData),
				"account_created": () => this.adaptAccountCreated(eventData),
			};

			if (!eventMap[eventName]) {
				return null;
			}

			return eventMap[eventName]();
		}

		/**
		 * Get the base OpenAI event data structure (timestamp, source URL, user).
		 *
		 * @returns {object}
		 */
		getBaseData() {

			// The Conversions API user data and the oppref click reference come
			// from functions_premium.js, which the free build does not ship. The
			// browser pixel only reads event_name, event_data and event_id.
			if (typeof pmw.getOpenAIBaseData === "function") {
				return pmw.getOpenAIBaseData();
			}

			return {
				timestamp_ms: Date.now(),
				source_url  : window.location.href,
			};
		}

		/**
		 * page_view -> page_viewed
		 */
		adaptPageView(eventData) {
			const data      = this.getBaseData();
			data.event_id   = pmw.getRandomEventId();
			data.event_name = "page_viewed";
			data.event_data = {
				type: "contents",
			};
			return data;
		}

		/**
		 * account_created -> registration_completed
		 *
		 * OpenAI's customer_action shape carries no contents; the event is the
		 * signal. Fired once per new account (pmw:account-created).
		 */
		adaptAccountCreated(eventData) {
			const data      = this.getBaseData();
			data.event_id   = pmw.getRandomEventId();
			data.event_name = "registration_completed";
			data.event_data = {
				type: "customer_action",
			};
			return data;
		}

		/**
		 * Build one contents[] item. The item-level amount is the per-unit price
		 * in minor units; the event-level amount stays the authoritative value.
		 *
		 * @param {object} item     Product or cart/order line item.
		 * @param {number} quantity
		 * @param {string} currency
		 * @return {object}
		 */
		buildContent(item, quantity, currency) {
			const content = {
				id          : pmw.getProductIdForSpecificPixel(item, "openai"),
				name        : item.name,
				content_type: "product",
				quantity    : quantity,
			};

			if (item.price !== undefined && item.price !== null && item.price !== "") {
				content.amount   = pmw.openaiToMinorUnits(item.price, currency);
				content.currency = currency;
			}

			return content;
		}

		/**
		 * view_item -> contents_viewed
		 */
		adaptViewContent(eventData) {
			if (!eventData?.product) return null;

			const product  = eventData.product;
			const currency = pmw.getProductCurrency(product);

			const data      = this.getBaseData();
			data.event_id   = pmw.getRandomEventId();
			data.event_name = "contents_viewed";
			data.event_data = {
				type    : "contents",
				amount  : pmw.openaiToMinorUnits(product.price, currency),
				currency: currency,
				contents: [ this.buildContent(product, 1, currency) ],
			};
			return data;
		}

		/**
		 * add_to_cart -> items_added
		 */
		adaptAddToCart(eventData) {
			if (!eventData?.product) return null;

			const product  = eventData.product;
			const quantity = product.quantity || 1;
			const currency = pmw.getProductCurrency(product);

			const data      = this.getBaseData();
			data.event_id   = pmw.getRandomEventId();
			data.event_name = "items_added";
			data.event_data = {
				type    : "contents",
				amount  : pmw.openaiToMinorUnits(product.price * quantity, currency),
				currency: currency,
				contents: [ this.buildContent(product, quantity, currency) ],
			};
			return data;
		}

		/**
		 * begin_checkout -> checkout_started
		 *
		 * begin_checkout dispatches with an empty eventData, so the value and line
		 * items are sourced from the cart (matching the Facebook/GA4 adapters). A
		 * value or items array explicitly passed in eventData takes precedence.
		 */
		adaptStartCheckout(eventData) {
			const currency = eventData.currency || pmwDataLayer?.shop?.currency;

			const cartItems = (pmwDataLayer?.cart && !pmw.isEmptyObject(pmwDataLayer.cart))
				? Object.values(pmwDataLayer.cart)
				: [];

			const items  = (Array.isArray(eventData.items) && eventData.items.length) ? eventData.items : cartItems;
			const amount = eventData.value !== undefined ? eventData.value : pmw.getCartValue();

			const data      = this.getBaseData();
			data.event_id   = pmw.getRandomEventId();
			data.event_name = "checkout_started";
			data.event_data = {
				type    : "contents",
				amount  : pmw.openaiToMinorUnits(amount || 0, currency),
				currency: currency,
				// A line whose product data is gone (a deleted product on an older
				// order) resolves to no id. Drop the line, keep the amount: it comes
				// from the order, so the conversion stays correct.
				contents: items
					.map(item => this.buildContent(item, item.quantity, currency))
					.filter(content => null !== content.id),
			};
			return data;
		}

		/**
		 * purchase -> order_created
		 *
		 * The purchase event dispatches { order: pmwDataLayer.order }; the order is
		 * read directly (matching the Facebook/TikTok adapters). The order value
		 * lives at order.value.marketing, and the event_id mirrors the server-side
		 * Conversions API id ("pmw_" + order ID) so the browser pixel and the CAPI
		 * purchase event are deduplicated.
		 */
		adaptPurchase(eventData) {
			const order = (eventData && eventData.order) || pmwDataLayer?.order;
			if (!order) return null;

			const currency = order.currency || pmwDataLayer?.shop?.currency;
			const items    = Array.isArray(order.items) ? order.items : Object.values(order.items || {});

			const data      = this.getBaseData();
			data.event_id   = order.id ? "pmw_" + order.id.toString() : pmw.getRandomEventId();
			data.event_name = "order_created";
			data.event_data = {
				type    : "contents",
				amount  : pmw.openaiToMinorUnits(order.value?.marketing ?? 0, currency),
				currency: currency,
				// A line whose product data is gone (a deleted product on an older
				// order) resolves to no id. Drop the line, keep the amount: it comes
				// from the order, so the conversion stays correct.
				contents: items
					.map(item => this.buildContent(item, item.quantity, currency))
					.filter(content => null !== content.id),
			};
			return data;
		}
	}

	// Register the adapter with S2S support
	pmw.registerAdapter("openai", new OpenAIAdapter(), {
		supportsS2S               : true,
		supportsEventDeduplication: true,
		supportsPageViewS2S       : true,
		isActive                  : () => pmwDataLayer?.pixels?.openai?.loaded === true && pmw.consent.categories.get().marketing === true,
		isConfigured              : () => !!pmwDataLayer?.pixels?.openai?.pixel_id,
	});

}(window.pmw = window.pmw || {}));
